import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, map, merge, Observable, of, switchMap, take, tap, timer } from 'rxjs';

import { FullStoryService, SEGMENT_EVENT_METADATA, SegmentContactTraits } from '@ninety/mobile/platform/services';

import { appActions } from '../../../app/app.actions';
import { capacitorAppActions } from '../../../capacitor/app/capacitor-app.actions';
import { SegmentActions } from '../segment/segment.actions';
import { SegmentSelectors } from '../segment/segment.selectors';
import { AnalyticsSessionActions } from '../session-cross-link/analytics-session.actions';

/**
 * Cadence for reaching the interface the native SDK injects. Neither user of it gates on the
 * interface explicitly: `grantConsent` and `getCurrentSessionUrl` both report failure until the
 * interface is there and working, so the poll itself is the wait.
 */
const INTERFACE_POLL_INTERVAL_MS = 1000;
const INTERFACE_POLL_ATTEMPTS = 10;

type IdentifiedContact = SegmentContactTraits & { id: string };

/**
 * Owns everything the app tells FullStory. The native SDKs are the only recorder — Segment's
 * device-mode FullStory destination is disabled in `SegmentService` — so the calls that destination
 * would otherwise make on the app's behalf are made here instead.
 */
@Injectable()
export class FullStoryEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly fullStory = inject(FullStoryService);

  /**
   * Triggered by subscription rather than an action — consent belongs to app start, and no
   * installed plugin emits a launch event.
   *
   * The native SDK injects its interface from outside the bundle, so it arrives after this class is
   * constructed — and may never arrive at all when capture is disabled. `take` on the timer bounds
   * that case.
   */
  grantConsent$ = createEffect(
    () =>
      timer(0, INTERFACE_POLL_INTERVAL_MS).pipe(
        take(INTERFACE_POLL_ATTEMPTS),
        map(() => this.fullStory.grantConsent()),
        filter(granted => granted),
        take(1)
      ),
    { dispatch: false }
  );

  /**
   * Covers both paths that identify a user: the identify at app start, and the feature-dispatched
   * `SegmentActions.identify` that enriches the user with extra traits. Re-identifying with the
   * same id is idempotent — FullStory only splits a session when the id differs — so firing on both
   * is safe.
   *
   * Traits are passed through unchanged: the interface takes arbitrary properties, with none of the
   * type-suffixed naming its predecessor required.
   */
  identifyUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.appAndCompanyReady, SegmentActions.identify),
        concatLatestFrom(() => this.store.select(SegmentSelectors.selectSegmentContactTraits)),
        map(([action, contact]) => ({ ...contact, ...('traits' in action ? action.traits : {}) })),
        filter((contact): contact is IdentifiedContact => !!contact.id),
        tap(({ id, ...traits }) => this.fullStory.identify(id, traits))
      ),
    { dispatch: false }
  );

  /**
   * Mirrors tracked events onto the FullStory session timeline. Both tracked-event actions are
   * covered because every event belongs on the timeline regardless of whether the user is
   * identified; they are dispatched from mutually exclusive surfaces (authenticated vs.
   * login/signup), so an event cannot arrive twice.
   *
   * Only the event's own params and registry metadata are sent — identity traits already ride on
   * the session from `identifyUser$`, and repeating them on every event is noise in the timeline.
   */
  mirrorTrackedEvents$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SegmentActions.track, SegmentActions.trackAnonymousEvent),
        tap(({ event, params }) =>
          this.fullStory.trackEvent(event, { ...params, ...(SEGMENT_EVENT_METADATA[event] ?? {}) })
        )
      ),
    { dispatch: false }
  );

  /**
   * `SegmentEffects.trackAppState$` calls the Segment service directly rather than dispatching a
   * tracked-event action, so `mirrorTrackedEvents$` never sees these. Foreground/background
   * transitions are load-bearing context on a session timeline, so they are mirrored here under the
   * names Segment sends.
   */
  mirrorAppState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(capacitorAppActions.appStateChanged),
        tap(({ isActive }) => this.fullStory.trackEvent(isActive ? 'App Opened' : 'App Backgrounded', {}))
      ),
    { dispatch: false }
  );

  /**
   * The first session has to be polled for rather than observed: the native SDK starts capturing
   * before this bundle loads, so that session began before anything here could register interest in
   * it. The poll also covers the URL not being readable the instant the interface appears.
   *
   * Holding a URL proves the interface is present, so the observer for subsequent sessions is
   * registered off the back of the first one rather than waiting on the interface separately.
   */
  announceSession$ = createEffect(() =>
    this.firstSessionUrl$().pipe(
      switchMap(firstUrl => merge(of(firstUrl), this.subsequentSessionUrls$())),
      distinctUntilChanged(),
      map(url => AnalyticsSessionActions.fullStorySessionDetected({ url }))
    )
  );

  private firstSessionUrl$(): Observable<string> {
    return timer(0, INTERFACE_POLL_INTERVAL_MS).pipe(
      take(INTERFACE_POLL_ATTEMPTS),
      map(() => this.fullStory.getCurrentSessionUrl()),
      filter((url): url is string => !!url),
      take(1)
    );
  }

  /** Registration can only fail if the interface went away since the first session was read. */
  private subsequentSessionUrls$(): Observable<string> {
    return new Observable<string>(subscriber => {
      const registered = this.fullStory.observeSessionStart(() => {
        const url = this.fullStory.getCurrentSessionUrl();
        if (url) subscriber.next(url);
      });

      if (!registered) subscriber.complete();
    });
  }
}
